/**
 * @fileoverview QR Code display component with print and share functionality.
 * @summary Displays a QR code for an event with print and share options.
 *
 * @package KaraoNow
 * @module apps/front/src/components/QRCodeDisplay
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPrint,
  faCopy,
  faShare,
  faDownload,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'

/**
 * @summary QR Code display props.
 */
interface QRCodeDisplayProps {
  /** QR code value (token). */
  qrCode: string
  /** Event name (for display). */
  eventName?: string
  /** Full URL to the event page. */
  eventUrl: string
}

/**
 * @summary QR Code display component.
 */
export default function QRCodeDisplay({ qrCode, eventName, eventUrl }: QRCodeDisplayProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const qrRef = useRef<HTMLDivElement>(null)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleShare = async () => {
    const shareText = eventName
      ? t('events.qrCodeShareText').replace('{{eventName}}', eventName)
      : t('events.qrCodeShareText').replace('{{eventName}}', '')
    const shareData = {
      title: eventName || t('events.qrCodeTitle'),
      text: shareText,
      url: eventUrl,
    }

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData)
      } else {
        // Fallback to copy
        handleCopyLink()
      }
    } catch (err) {
      // User cancelled or error - fallback to copy
      if ((err as Error).name !== 'AbortError') {
        handleCopyLink()
      }
    }
  }

  const handlePrint = () => {
    if (!qrRef.current) return

    // Use a hidden iframe for better print control
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.style.opacity = '0'
    iframe.style.pointerEvents = 'none'
    document.body.appendChild(iframe)

    const printWindow = iframe.contentWindow
    if (!printWindow) {
      document.body.removeChild(iframe)
      return
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('events.qrCodePrintTitle')}</title>
          <style>
            @page {
              margin: 0;
              size: A4 portrait;
            }
            @media print {
              @page {
                margin: 0;
                size: A4 portrait;
              }
              * {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              html, body {
                margin: 0;
                padding: 0;
                width: 100%;
                height: 100%;
                overflow: hidden;
              }
              body {
                page-break-after: avoid;
                page-break-inside: avoid;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 20mm;
                box-sizing: border-box;
              }
              .print-container {
                page-break-after: avoid;
                page-break-inside: avoid;
                width: 100%;
                max-width: 100%;
              }
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 20px;
              margin: 0;
              min-height: auto;
            }
            .print-container {
              text-align: center;
              width: 100%;
              max-width: 100%;
            }
            .event-name {
              font-size: 40px;
              font-weight: bold;
              margin-bottom: 40px;
              page-break-after: avoid;
            }
            .qr-code {
              margin: 200px auto;
              padding: 20px;
              background: white;
              border: 2px solid #000;
              display: inline-block;
              page-break-inside: avoid;
            }
            .url {
              margin-top: 20px;
              font-size: 14px;
              word-break: break-all;
              color: #666;
              page-break-before: avoid;
            }
            .instructions {
              margin-top: 30px;
              font-size: 24px;
              color: #333;
              page-break-before: avoid;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${eventName ? `<div class="event-name">${eventName}</div>` : ''}
            <div class="qr-code">
              ${qrRef.current.querySelector('svg')?.outerHTML || ''}
            </div>
           <!-- <div class="url">${eventUrl}</div> -->
            <div class="instructions">${t('events.qrCodePrintInstructions')}</div>
          </div>
        </body>
      </html>
    `

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
    if (!iframeDoc) {
      document.body.removeChild(iframe)
      return
    }

    iframeDoc.open()
    iframeDoc.write(html)
    iframeDoc.close()

    // Wait for content to load before printing
    let hasPrinted = false
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null

    const doPrint = () => {
      if (hasPrinted) return
      hasPrinted = true
      if (fallbackTimer) {
        clearTimeout(fallbackTimer)
        fallbackTimer = null
      }
      setTimeout(() => {
        printWindow.focus()
        printWindow.print()
        // Clean up iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe)
        }, 1000)
      }, 250)
    }

    // Use onload if available
    const handleLoad = () => {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer)
        fallbackTimer = null
      }
      doPrint()
    }

    iframe.onload = handleLoad

    // Fallback only if onload doesn't trigger after a reasonable delay
    fallbackTimer = setTimeout(() => {
      if (!hasPrinted && iframeDoc.readyState === 'complete') {
        doPrint()
      }
    }, 1000)
  }

  const handleDownload = () => {
    if (!qrRef.current) return

    const svg = qrRef.current.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx?.drawImage(img, 0, 0)
      canvas.toBlob((blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `qrcode-${qrCode}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      })
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  return (
    <div className="card bg-(--color-primary-50) dark:bg-(--color-primary-900)/20 border-(--color-primary-200) dark:border-(--color-primary-800)">
      <div className="flex flex-col md:flex-row gap-6">
        {/* QR Code */}
        <div className="shrink-0 flex flex-col items-center">
          <div
            ref={qrRef}
            className="bg-white p-4 rounded-lg border-2 border-gray-300 dark:border-gray-600"
          >
            <QRCodeSVG
              value={eventUrl}
              size={200}
              level="H"
              includeMargin={false}
              fgColor="#000000"
              bgColor="#ffffff"
            />
          </div>
          {eventName && (
            <p className="mt-3 text-sm font-semibold text-gray-900 dark:text-white text-center max-w-[200px]">
              {eventName}
            </p>
          )}
        </div>

        {/* Info and Actions */}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-(--color-text-primary) mb-2">
            {t('events.qrCodeTitle')}
          </h3>
          <p className="text-sm text-(--color-text-secondary) mb-4">
            {t('events.qrCodeDescription')}
          </p>

          {/* URL */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-(--color-text-secondary) mb-1">
              {t('events.qrCodeUrl')}
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white dark:bg-gray-800 p-2 text-(--color-primary) dark:text-(--color-primary-400) rounded border border-gray-300 dark:border-gray-700 text-xs font-mono break-all">
                {eventUrl}
              </code>
              <button
                onClick={handleCopyLink}
                className="btn btn-outline p-2 shrink-0"
                title={t('events.copyQRCode')}
              >
                {copied ? (
                  <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-600" />
                ) : (
                  <FontAwesomeIcon icon={faCopy} className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button onClick={handlePrint} className="btn btn-primary flex items-center gap-2">
              <FontAwesomeIcon icon={faPrint} className="w-4 h-4" />
              {t('events.qrCodePrint')}
            </button>
            <button onClick={handleShare} className="btn flex-1 btn-outline flex items-center gap-2">
              <FontAwesomeIcon icon={faShare} className="w-4 h-4" />
              {t('events.qrCodeShare')}
            </button>
            <button onClick={handleDownload} className="btn flex-1 btn-outline flex items-center gap-2">
              <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
              {t('events.qrCodeDownload')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

